import { Spacing } from '@persona-id/design-tokens';
import { groupBy, partition, uniq } from 'lodash/fp';
import styled from 'styled-components';

import { Environment } from 'dashboard/models/environment';
import { RolePermission } from 'dashboard/models/role';
import { PERMISSIONS_WITH_ENVIRONMENT } from 'dashboard/views/DashboardRolesShowView/constants';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { Permission } from 'lib/permissions';

export interface EnvironmentsAdvancedSettingsProps {
  disabled: boolean;
  isAdmin: boolean;
  environments: Environment[];
  allCurrentAndFutureEnvironmentSelected: boolean;
  rolePermissions: RolePermission[];
  setRolePermissions: (permissions: RolePermission[]) => void;
}

const EnvironmentsAdvancedSettings = ({
  disabled,
  isAdmin,
  environments,
  allCurrentAndFutureEnvironmentSelected,
  rolePermissions,
  setRolePermissions,
}: EnvironmentsAdvancedSettingsProps) => {
  const handleCheckbox = () => {
    const [rolePermissionsWithoutEnvironmentScope, rolePermissionsWithEnvironmentScope] = partition(
      (rp) => !PERMISSIONS_WITH_ENVIRONMENT.includes(rp.name),
      rolePermissions.filter((rp) => rp.name !== Permission.EnvironmentView),
    );

    if (allCurrentAndFutureEnvironmentSelected) {
      const newEnvironmentViewRolePermissions = environments.map((environment) => {
        return {
          name: Permission.EnvironmentView,
          'environment-id': environment.id,
          'object-id': null,
        };
      });

      const newRolePermissions = environments.flatMap((environment) => {
        return rolePermissionsWithEnvironmentScope.map((rp) => {
          return { ...rp, 'environment-id': environment.id };
        });
      });

      setRolePermissions([
        ...rolePermissionsWithoutEnvironmentScope,
        ...newRolePermissions,
        ...newEnvironmentViewRolePermissions,
      ]);
    } else {
      const [rolePermissionsWithObjectId, rolePermissionsWithoutObjectId] = partition(
        (rp) => rp['object-id'] != null,
        rolePermissionsWithEnvironmentScope,
      );

      const nameToRolePermissions = groupBy((rp) => rp.name, rolePermissionsWithObjectId);

      const newRolePermissionsWithObjectScope: RolePermission[] = Object.keys(
        nameToRolePermissions,
      ).flatMap((name) => {
        return uniq(nameToRolePermissions[name].map((rp) => rp['object-id'])).flatMap(
          (objectId) => {
            return {
              name: name as Permission,
              'environment-id': null,
              objectId,
            };
          },
        );
      });

      const newRolePermissionsWithoutObjectId = uniq(
        rolePermissionsWithoutObjectId.map((rp) => rp.name),
      ).map((name) => {
        return { name, 'environment-id': null, 'object-id': null };
      });

      const newEnvironmentViewRolePermission: RolePermission = {
        name: Permission.EnvironmentView,
        'environment-id': null,
        'object-id': null,
      };

      setRolePermissions([
        ...rolePermissionsWithoutEnvironmentScope,
        ...newRolePermissionsWithObjectScope,
        ...newRolePermissionsWithoutObjectId,
        newEnvironmentViewRolePermission,
      ]);
    }
  };

  return (
    <>
      <Card.SectionHeader>Advanced Settings</Card.SectionHeader>
      <div>
        <StyledRow>
          <StyledCheckboxCell>
            <Checkbox
              id='permissions-table__environments-advanced-settings-checkbox'
              checked={isAdmin || allCurrentAndFutureEnvironmentSelected}
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCheckbox();
              }}
            />
          </StyledCheckboxCell>
          <div>
            <StyledParagraph size='sm' color='primary'>
              Enable all current and new environments for role
            </StyledParagraph>
            <Paragraph>
              Allows this role to automatically access all current and new environments
            </Paragraph>
          </div>
        </StyledRow>
        <Spacer height={2} />
      </div>
    </>
  );
};

export default EnvironmentsAdvancedSettings;

const StyledParagraph = styled(Paragraph)`
  font-weight: 500;
`;

const StyledRow = styled.div`
  display: flex;
`;

const StyledCheckboxCell = styled.div`
  height: ${Spacing[6]};
  width: ${Spacing[7]};
  margin-top: 4px;
  margin-left: 6px;
`;
